import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Play, Eye, Heart, MessageSquare, CheckCircle2, Check } from 'lucide-react';
import { isVideoWatched, toggleWatchedStatus } from './tabs/watchedManager'; // 👈 Adicione esta linha logo abaixo

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  category?: string;
  is_premium?: boolean;
  views_count?: number;
  likes_count?: number;
  comments_count?: number;
}

interface CategoryRowProps {
  title: string;
  videos: Video[];
  onSelectVideo: (id: string) => void;
  onViewAll?: (categoryTitle: string, videosList: Video[]) => void; // 👈 ADICIONE O videosList AQUI
}

// Formata números grandes (ex: 1400 -> 1,4 mil | 1500000 -> 1,5M)
const formatCount = (count?: number) => {
  if (count === undefined || count === null || isNaN(count)) return '0';
  
  // Arredonda para baixo caso venha algum número quebrado do banco
  const num = Math.floor(count);

  if (num < 0) return '0';
  if (num >= 1000000000) return (num / 1000000000).toFixed(1).replace('.', ',') + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace('.', ',') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace('.', ',') + ' mil';
  
  return num.toString();
};

export default function CategoryRow({ title, videos, onSelectVideo, onViewAll }: CategoryRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [hoveredVideoId, setHoveredVideoId] = useState<string | null>(null);

  // 🟢 ADICIONE APENAS ESTA LINHA DE ESTADO:
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});

  // 👇 COLE ESTES TRÊS PASSOS AQUI DENTRO:
  const [, setForceUpdate] = useState(0);

  useEffect(() => {
    const handleWatchedChange = () => setForceUpdate(prev => prev + 1);
    window.addEventListener('doisplay_watched_updated', handleWatchedChange);
    return () => window.removeEventListener('doisplay_watched_updated', handleWatchedChange);
  }, []);
  // 👆 FIM DA ADIÇÃO

  const handleScroll = (direction: 'left' | 'right') => {
    if (rowRef.current) {
      const { scrollLeft, clientWidth } = rowRef.current;
      const scrollAmount = clientWidth * 0.75;
      rowRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!videos || videos.length === 0) return null;

  const limitedVideos = videos.slice(0, 10);

  // Pega o nome da categoria limpo para usar na etiqueta caso o vídeo não tenha
  const categoryFallback = title.replace(/[^a-zA-ZÀ-ÿ\s]/g, '').trim();

  return (
    <div className="row-container" style={{ marginBottom: '30px', width: '100%', boxSizing: 'border-box' }}>
      
      {/* CSS Embutido para Responsividade Profissional */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        /* Padrão para Computador */
        .row-container { padding-left: 40px; padding-right:40px; }
        .video-card { min-width: 250px; width:150px; }
        .scroll-btn { display: flex; }
        .title-text { font-size: 14px; margin: 8px 0 4px 0; }
        .metrics-container { gap: 10px; flex-wrap: nowrap; }
        .metrics-item { font-size: 11px; }
        .category-tag { font-size: 11px; }

        /* Ajustes IDÊNTICOS ao Print para Celular */
        @media (max-width:768px) {
          .row-container { padding-left: 15px !important; padding-right: 15px !important; marginBottom: 25px !important; }
          .video-card { min-width: 155px !important; width: 105px !important; } /* Cabe 3 por tela */
          .scroll-btn { display: none !important; } /* Esconde as setas, usa-se o touch */
          .title-text { font-size: 12px !important; margin: 6px 0 4px 0 !important; }
          .metrics-container { gap: 4px !important; flex-wrap: wrap !important; }
          .metrics-item { font-size: 10px !important; }
          .category-tag { font-size: 9px !important; }
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .image-skeleton {
          background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: 'clamp(16px, 4vw, 18px)', fontWeight: '800', color: '#fff', margin: 0, letterSpacing: '0.5px' }}>
          {title}
        </h3>
        {onViewAll && (
          <span 
            onClick={() => onViewAll(title, videos)} // 👈 ENVIE A LISTA DE VÍDEOS JUNTO COM O TÍTULO
            style={{ fontSize: '13px', color: '#E50914', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            Ver todos <ArrowRight size={14} />
          </span>
        )}
      </div>

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        
        {/* Seta Esquerda (Apenas PC) */}
        <button 
          className="scroll-btn"
          onClick={() => handleScroll('left')}
          style={{ 
            position: 'absolute', left: '-20px', zIndex: 10, background: 'rgba(0,0,0,0.75)', 
            border: '1px solid rgba(255,255,255,0.15)', color: '#fff', width: '35px', height: '60px', 
            cursor: 'pointer', borderRadius: '4px', alignItems: 'center', justifyContent: 'center'
          }}>
          <ChevronLeft size={22} />
        </button>

        <div 
          ref={rowRef}
          className="no-scrollbar"
          style={{ 
            display: 'flex', gap: '12px', overflowX: 'auto', 
            paddingBottom: '10px', paddingTop: '5px', width: '100%', scrollBehavior: 'smooth' 
          }}>
          {limitedVideos.map((video) => {
            const isHovered = hoveredVideoId === video.id;

            return (
              <div 
                key={video.id} 
                className="video-card"
                onClick={() => onSelectVideo(video.id)}
                onMouseEnter={() => setHoveredVideoId(video.id)}
                onMouseLeave={() => setHoveredVideoId(null)}
                style={{ 
                  cursor: 'pointer', 
                  flexShrink: 0, 
                  transition: 'transform 0.3s cubic-bezier(0.5, 0, 0.1, 1)',
                  transform: isHovered ? 'scale(1.04) translateY(-4px)' : 'scale(1)',
                  zIndex: isHovered ? 20 : 1,
                  position: 'relative'
                }}>
                
               {/* Contêiner da Imagem */}
                <div style={{ width: '100%', aspectRatio: '4/2.5', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#141414', position: 'relative', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                  
                  {/* Fundo cinza animado (Skeleton) enquanto a imagem baixa */}
                  {!loadedImages[video.id] && (
                    <div className="image-skeleton" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
                  )}

                  <img 
                    src={video.thumbnail || 'https://via.placeholder.com/200x300?text=Sem+Capa'} 
                    alt={video.title} 
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setLoadedImages(prev => ({ ...prev, [video.id]: true }))}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      opacity: loadedImages[video.id] ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      position: 'relative',
                      zIndex: 2
                    }}
                  />

                  {/* Botão de Assistido */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWatchedStatus(video.id);
                    }}
                    title={isVideoWatched(video.id) ? "Marcar como não assistido" : "Marcar como assistido"}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      left: '6px',
                      backgroundColor: isVideoWatched(video.id) ? '#22c55e' : 'rgba(0,0,0,0.6)',
                      color: '#262323',
                      border: 'none',
                      borderRadius: '4px',
                      width: '15px',
                      height: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 5,
                      backdropFilter: 'blur(4px)',
                      transition: 'background 0.2s'
                    }}
                  >
                    {isVideoWatched(video.id) ? <CheckCircle2 size={16} color="#fff" /> : <Check size={14} color="#2e2b2b" />}
                  </button>

                  {/* 🟢 ETIQUETA DE TIPO NA CAPA (Trailer vs Filme Completo) */}
                  {/* 🟢 ETIQUETA DE TIPO NA CAPA (Trailer vs Game vs Filme) */}
          <div style={{ 
            position: 'absolute', 
            top: '6px', /* No CategoryRow pode ser 8px */
            right: video.is_premium ? '42px' : '6px', /* Ajuste conforme o arquivo */
            backgroundColor: video.category?.toLowerCase().includes('trailer') ? 'rgba(229, 9, 20, 0.9)' : 'rgba(0, 0, 0, 0.85)', 
            color: '#fff', 
            fontSize: '8px', 
            fontWeight: 'bold', 
            padding: '2px 5px', 
            borderRadius: '3px', 
            zIndex: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            border: '1px solid rgba(255,255,255,0.15)'
          }}>
            {video.category?.toLowerCase().includes('trailer') 
              ? '🎬 Trailer' 
              : video.category?.toLowerCase().includes('game') 
                ? '🎮 Game' 
                : '🎥 Filme'}
          </div>

                  {/* Etiqueta de Categoria no fundo da imagem */}
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.95))', padding: '25px 8px 6px 8px', display: 'flex', alignItems: 'flex-end', zIndex: 3 }}>
                    <span className="category-tag" style={{ color: '#E50914', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {video.category?.includes('trailer') 
                        ? '🎬 Trailer Oficial' 
                        : (video.category || categoryFallback)?.replace('filmes:', '').replace('_', ' ')}
                    </span>
                  </div>
                

                  {isHovered && (
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div style={{ backgroundColor: '#E50914', borderRadius: '50%', padding: '10px', color: '#fff', display: 'flex' }}>
                        <Play size={18} fill="#fff" />
                      </div>
                    </div>
                  )}

                  {video.is_premium && (
                    <span style={{ position: 'absolute', top: '6px', right: '6px', backgroundColor: '#E50914', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 'bold' }}>
                      VIP
                    </span>
                  )}
                </div>
                
                {/* Título limitando a 2 linhas com "..." no final caso seja longo */}
                <div>
                  <h4 className="title-text" style={{ 
                    fontWeight: '600', 
                    color: isHovered ? '#fff' : '#ccc', 
                    display: '-webkit-box', 
                    WebkitLineClamp: 2, 
                    WebkitBoxOrient: 'vertical', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    lineHeight: '1.2' 
                  }}>
                    {video.title}
                  </h4>
                  
                  {/* Quantidades ajustadas para quebrarem linha de forma limpa no celular */}
                  <div className="metrics-container" style={{ display: 'flex', alignItems: 'center', color: '#888' }}>
                    <span className="metrics-item" style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Eye size={12} color="#aaa" /> {formatCount(video.views_count ?? 0)}
                    </span>
                    <span className="metrics-item" style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Heart size={12} color="#E50914" /> {formatCount(video.likes_count ?? 0)}
                    </span>
                    <span className="metrics-item" style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <MessageSquare size={12} color="#3b82f6" /> {formatCount(video.comments_count ?? 0)}
                    </span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>

        {/* Seta Direita (Apenas PC) */}
        <button 
          className="scroll-btn"
          onClick={() => handleScroll('right')}
          style={{ 
            position: 'absolute', right: '-20px', zIndex: 10, background: 'rgba(0,0,0,0.75)', 
            border: '1px solid rgba(255,255,255,0.15)', color: '#fff', width: '35px', height: '60px', 
            cursor: 'pointer', borderRadius: '4px', alignItems: 'center', justifyContent: 'center'
          }}>
          <ChevronRight size={22} />
        </button>

      </div>
    </div>
  );
}